import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useApi } from "../utils/Api";
import ErrorModal from "../components/ErrorModal";


function ContactPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [isMapLoading, setIsMapLoading] = useState(true);
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { apiFetch } = useApi();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { name, email, message } = formData;
    setIsLoading(true);
    try {
      const result = await apiFetch("send-contact", { name, email, message });
      if (result.success) {
        setSubmitSuccess(true);
        if (typeof window.gtag === "function") {
          window.gtag("event", "contact_form_submit");
        }
      } else {
        setErrorMessage("There was an error sending your message. Please make sure all fields are correct.");
      }
    } catch (error) {
      setErrorMessage("An unexpected error occurred. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendAnother = () => {
    setFormData({ name: "", email: "", message: "" });
    setSubmitSuccess(false);
  };

  return (
    <div className="page-container overflow-y-auto standard-padding">
      <Helmet>
        <title>Contact | LasGidi Restaurant</title>
        <meta name="description" content="Get in touch with LasGidi Restaurant in Manchester. Find our location, call us, or send a message for bookings and inquiries." />
        <meta name="keywords" content="LasGidi Restaurant, Contact, Manchester Restaurant, Bookings, Inquiries, Nigerian Cuisine" />
        <meta property="og:title" content="Contact | LasGidi Restaurant" />
        <meta property="og:description" content="Get in touch with LasGidi Restaurant in Manchester. Find our location, call us, or send a message for bookings and inquiries." />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Map */}
      <div className="w-full h-[500px] mx-auto p-0 sm:p-4 relative shadow-[rgba(255,255,255,0.15)_0px_12px_36px_0px] overflow-hidden rounded-2xl">
        {isMapLoading && (
          <div className="absolute inset-0 bg-white/80 flex justify-center items-center z-10">
            <div className="border-[5px] border-[#f3f3f3] border-t-[#007bff] rounded-full w-10 h-10 animate-spin" />
          </div>
        )}
        <iframe
          title="What3Words Map"
          src="https://what3words.com/palace.piper.dame"
          className="w-[125%] max-lg:w-[145%] max-md:w-[160%] max-sm:w-[175%] h-full border-none ml-0"
          allowFullScreen
          loading="lazy"
          onLoad={() => setIsMapLoading(false)}
        />
      </div>

      {/* Form spinner overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-[9999]">
          <div className="border-4 border-white/30 border-t-white rounded-full w-10 h-10 animate-spin" />
        </div>
      )}

      {errorMessage && (
        <ErrorModal message={errorMessage} onClose={() => setErrorMessage("")} />
      )}

      {/* Contact info + form */}
      <div className="flex flex-col flex-wrap justify-around mt-16 pb-20">
        {/* Contact boxes */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-10 bg-black py-10 px-0 sm:px-5 w-full max-w-[1000px] mx-auto">
          {[
            { icon: "fas fa-map-marker-alt", title: "Address", content: <p className="text-white text-base">34, Wellington Road, <br />Eccles M30 0NP</p> },
            { icon: "fas fa-phone-alt", title: "Call", content: <p className="text-white text-base"><a href="tel:01615660344" className="text-white no-underline hover:underline">0161 566 0344</a></p> },
            { icon: "far fa-envelope", title: "E-mail", content: <p className="text-white text-base"><a href="mailto:restaurantlasgidi@gmail.com" className="text-white no-underline hover:underline">restaurantlasgidi@gmail.com</a></p> },
            { icon: "far fa-clock", title: "Opening Hours", content: <p className="text-white text-base">Open Daily: 1pm - 10pm</p> },
          ].map(({ icon, title, content }, i) => (
            <div key={i} className="text-center">
              <i className={`${icon} text-brand-orange text-5xl mb-2.5`} />
              <div className="break-words">
                <h3 className="mb-2.5 text-white text-base text-center font-bold">{title}</h3>
                {content}
              </div>
            </div>
          ))}
        </div>

        {/* Contact form */}
        <div className="w-full max-w-[500px] px-0 sm:px-[30px] py-[30px] mx-auto my-8 rounded-[20px] bg-black" id="message-form">
          {submitSuccess ? (
            <div className="text-center flex flex-col justify-center items-center py-5">
              <h2 className="text-[#28a745] mb-2.5">🎉 Message Sent!</h2>
              <p className="text-base text-white">
                Thank you for getting in touch with Lasgidi Restaurant. Your submission has been received — you'll be contacted soon.
              </p>
              <button
                className="mt-5 px-5 py-2.5 bg-brand-orange text-black font-bold border-none text-base cursor-pointer rounded transition-opacity duration-300 hover:opacity-70 font-[inherit]"
                onClick={handleSendAnother}
              >
                Send Another Message
              </button>
            </div>
          ) : (
            <div className="relative">
              <h3 className="text-white font-prata tracking-[0.5px] mb-8 text-2xl text-left">Send us a message</h3>
              <form onSubmit={handleSubmit} className="font-prata">
                {[
                  { type: "text", name: "name", id: "name", placeholder: "Name", label: "Name" },
                  { type: "email", name: "email", id: "email", placeholder: "Email Address", label: "Email Address" },
                ].map(({ type, name, id, placeholder, label }) => (
                  <div key={id} className="mb-8 relative">
                    <input
                      type={type}
                      name={name}
                      id={id}
                      placeholder={placeholder}
                      required
                      autoComplete="off"
                      value={formData[name]}
                      onChange={handleChange}
                      className="w-full block px-2 py-[15px] outline-none border-b-[3px] font-inherit text-base bg-white text-black placeholder-gray-500 peer border-green-500 placeholder-shown:border-transparent focus:placeholder-shown:border-red-500 focus:border-green-500 transition-all duration-200"
                    />
                    <label
                      htmlFor={id}
                      className="absolute left-0 top-full text-white text-base transition-all duration-300 opacity-0 pointer-events-none peer-[:not(:placeholder-shown)]:opacity-100 mt-1 font-inherit"
                    >
                      {label}
                    </label>
                  </div>
                ))}

                <div className="mb-8 relative">
                  <textarea
                    rows="6"
                    name="message"
                    id="message"
                    placeholder="Message"
                    required
                    autoComplete="off"
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full block px-2 py-[15px] outline-none border-b-[3px] font-inherit text-base bg-white text-black placeholder-gray-500 peer border-green-500 placeholder-shown:border-transparent focus:placeholder-shown:border-red-500 focus:border-green-500 transition-all duration-200 resize-none"
                  />
                  <label
                    htmlFor="message"
                    className="absolute left-0 top-full text-white text-base transition-all duration-300 opacity-0 pointer-events-none peer-[:not(:placeholder-shown)]:opacity-100 mt-1 font-inherit"
                  >
                    Message
                  </label>
                </div>

                <button
                  type="submit"
                  className="block mr-auto ml-0 mt-10 px-8 py-3 bg-brand-orange text-black border-none text-lg font-bold cursor-pointer rounded-md transition-opacity duration-300 hover:opacity-70 disabled:opacity-50 font-inherit"
                  disabled={isLoading}
                >
                  {isLoading ? "Submitting..." : "Submit"}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ContactPage;
